import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, Focus, X, Mail } from "lucide-react";
import { FocusMode } from "./focus/FocusMode";
import { LeadDraftCard, blockReason, chars, digits } from "./LeadDraftCard";

const STEP = 20;
const SESSION = 20;
const MAX_SEND = 25;

// Bottom workspace of the Follow-Up Queue: AI text drafts in the page's sort order, multi-select, send together.
export const LeadTextDrafts = ({ leads, sel, setSel, sl, onOpen, onSent, invalidate, gingrUrl }) => {
  const [count, setCount] = useState(STEP);
  const [drafts, setDrafts] = useState({});
  const [open, setOpen] = useState(false);
  const asked = useRef(new Set());
  const texts = useRef({});
  const onText = useCallback((id, t) => { texts.current[id] = t; }, []);
  const [phones, setPhones] = useState({});
  const onPhone = useCallback((id, v) => setPhones((p) => ({ ...p, [id]: v })), []);
  const blk = (id) => blockReason(drafts[id], phones[id]);
  const [focusQ, setFocusQ] = useState(null);
  const [summary, setSummary] = useState(null);
  const [reviewedOnly, setReviewedOnly] = useState(false);
  const isRev = (id) => !!drafts[id]?.reviewed_at;
  const shown = reviewedOnly ? leads.filter((l) => isRev(l.id)) : leads.slice(0, count);
  const shownKey = shown.map((l) => l.id).join(",");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!invalidate?.length) return;
    invalidate.forEach((id) => { asked.current.delete(id); delete texts.current[id]; });
    setDrafts((p) => { const n = { ...p }; invalidate.forEach((id) => delete n[id]); return n; });
    setTick((t) => t + 1);
  }, [invalidate]);

  const ensure = (ids) => {
    const need = ids.filter((id) => !asked.current.has(id));
    if (!need.length) return;
    need.forEach((id) => asked.current.add(id));
    for (let i = 0; i < need.length; i += 5) {
      const chunk = need.slice(i, i + 5);
      sl.batchDrafts(chunk)
        .then((items) => setDrafts((p) => ({ ...p, ...Object.fromEntries(items.map((it) => [it.lead_id, it])) })))
        .catch(() => chunk.forEach((id) => asked.current.delete(id)));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ensure(shown.map((l) => l.id)); }, [shownKey, tick]);

  const pool = (mode) => leads.filter((l) => {
    const d = drafts[l.id];
    if (!d || d.error || d.paused || isRev(l.id)) return false;
    return mode === "sms" ? !blk(l.id) : !d.sms_ok && d.email_ok;
  });
  const smsPool = pool("sms");
  const emailPool = pool("email");
  const [focusMode, setFocusMode] = useState("sms");
  const startFocus = (mode) => { setSummary(null); setFocusMode(mode); setFocusQ((mode === "sms" ? smsPool : emailPool).slice(0, SESSION)); };
  const onUpdate = useCallback((id, patch) => {
    setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
    if (patch.text) texts.current[id] = patch.text;
  }, []);
  const exitFocus = (stats) => {
    const ids = focusQ.map((l) => l.id).filter((id) => isRev(id) && !blk(id) && drafts[id]?.reviewed_channel !== "email");
    setSel((s) => ({ ...s, ...Object.fromEntries(ids.map((id) => [id, true])) }));
    setFocusQ(null);
    setSummary({ ...stats, mode: focusMode });
    if (stats.reviewed) setReviewedOnly(true);
  };

  const sendable = shown.filter((l) => !blk(l.id)).map((l) => l.id);
  const picks = Object.keys(sel).filter((id) => sel[id] && !blk(id));
  const bad = picks.filter((id) => { const t = (texts.current[id] ?? drafts[id]?.text ?? "").trim(); return !t || chars(t) > 320; });
  const allOn = sendable.length > 0 && sendable.every((id) => sel[id]);
  const toggleAll = () => setSel((s) => ({ ...s, ...Object.fromEntries(sendable.map((id) => [id, !allOn])) }));
  const save = async (id, text) => { const r = await sl.saveDraft(id, text); if (r) setDrafts((p) => ({ ...p, [id]: { ...p[id], text, edited: true } })); };
  const sendIds = async (ids) => {
    const items = ids.map((id) => {
      const d = drafts[id];
      const it = { lead_id: id, message: (texts.current[id] ?? d.text).trim() };
      if (!d.can_send_via_gingr) it.phone = digits(phones[id] ?? d.phone);
      return it;
    });
    const r = await sl.sendMany(items, onSent);
    if (r === null) return;
    ids.forEach((id) => { asked.current.delete(id); delete texts.current[id]; });
    setDrafts((p) => { const n = { ...p }; ids.forEach((id) => delete n[id]); return n; });
    setSel((s) => { const n = { ...s }; ids.forEach((id) => delete n[id]); return n; });
  };
  const send = () => sendIds(picks);
  const names = picks.map((id) => leads.find((l) => l.id === id)?.name || id);

  return (
    <div className="space-y-3" data-testid="lead-text-drafts">
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <h2 className="text-base md:text-lg font-bold text-slate-900">Text drafts <span className="text-sm font-normal text-slate-500">— pick who to text, edit if needed, then send together</span></h2>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button onClick={() => startFocus("sms")} disabled={!smsPool.length} className="rounded-full bg-indigo-600 hover:bg-indigo-700 shadow" data-testid="focus-start-sms-btn">
            <Focus className="w-4 h-4 mr-1.5" />Focus: Texts ({Math.min(SESSION, smsPool.length)})
          </Button>
          <Button onClick={() => startFocus("email")} disabled={!emailPool.length} variant="outline" className="rounded-full border-sky-300 text-sky-700" data-testid="focus-start-email-btn">
            <Mail className="w-4 h-4 mr-1.5" />Focus: Emails ({Math.min(SESSION, emailPool.length)})
          </Button>
        </div>
      </div>
      {summary && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-xl px-4 py-2" data-testid="focus-summary">
          <span>{summary.mode === "email" ? "Email" : "Text"} focus session done: <b>{summary.reviewed} reviewed</b> ({summary.edited} edited) · {summary.skipped} skipped. {summary.mode === "email" ? "Reviewed emails are saved — use Write email on each card to copy/open them until Microsoft 365 is connected." : "Reviewed texts are pre-selected below — check and send."}</span>
          <button className="ml-auto text-emerald-700" onClick={() => setSummary(null)} data-testid="focus-summary-close"><X className="w-4 h-4" /></button>
        </div>
      )}
      {focusQ && createPortal(<FocusMode mode={focusMode} queue={focusQ} drafts={drafts} ensure={ensure} onUpdate={onUpdate} gingrUrl={gingrUrl} onExit={exitFocus} />, document.body)}
      <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl px-3 py-2 shadow-sm" data-testid="lead-drafts-bulk-bar">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
          <Checkbox checked={allOn} disabled={!sendable.length} onCheckedChange={toggleAll} data-testid="lead-drafts-select-all" />
          Select all with SMS ✓
        </label>
        <span className="text-xs text-slate-500" data-testid="lead-drafts-selected-count">{picks.length} selected</span>
        <div className="flex text-xs rounded-full bg-slate-100 p-0.5" data-testid="lead-drafts-show-toggle">
          {[["All", false], ["Reviewed", true]].map(([label, v]) => (
            <button key={label} onClick={() => setReviewedOnly(v)} data-testid={`lead-drafts-show-${label.toLowerCase()}`}
              className={`px-2.5 py-0.5 rounded-full font-semibold ${reviewedOnly === v ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{label}</button>
          ))}
        </div>
        <Button size="sm" className="ml-auto bg-gradient-to-r from-emerald-500 to-emerald-600"
          disabled={!picks.length || picks.length > MAX_SEND || bad.length > 0 || sl.busy === "send"} onClick={() => setOpen(true)} data-testid="lead-drafts-send-btn">
          <Send className="w-3.5 h-3.5 mr-1" />Send {picks.length || ""} via Gingr
        </Button>
        {picks.length > MAX_SEND && <span className="w-full text-xs text-rose-600">Max {MAX_SEND} texts per send — unselect a few.</span>}
        {bad.length > 0 && <span className="w-full text-xs text-rose-600">{bad.length} selected draft(s) are empty or over 320 characters.</span>}
      </div>
      {shown.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl py-12 text-center text-slate-400 text-sm">{reviewedOnly ? "No reviewed drafts yet — start focus mode." : "No leads match the current filters."}</div>}
      {shown.map((l) => (
        <LeadDraftCard key={l.id} lead={l} d={drafts[l.id]} checked={!!sel[l.id]} onText={onText} onSave={save} onOpen={onOpen}
          onSend={() => sendIds([l.id])} sending={sl.busy === "send"} phone={phones[l.id]} onPhone={onPhone}
          onCheck={() => setSel((s) => ({ ...s, [l.id]: !s[l.id] }))} />
      ))}
      {!reviewedOnly && count < leads.length && (
        <div className="text-center">
          <Button variant="outline" onClick={() => setCount((c) => c + STEP)} data-testid="lead-drafts-load-more">Load {Math.min(STEP, leads.length - count)} more ({leads.length - count} left)</Button>
        </div>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="lead-drafts-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Text {picks.length} lead{picks.length === 1 ? "" : "s"} via Gingr?</AlertDialogTitle>
            <AlertDialogDescription>Each lead gets their own draft exactly as shown, one after another: {names.join(", ")}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-drafts-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="lead-drafts-confirm-send">Send all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
